import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'
import { Model } from 'modules/core/lib/mobx-rest'

import type Plan from 'modules/billing/store/models/Plan'

interface ChangePlanProps {
  upgradePlan: Plan
}

export interface DueBannerState {
  status: 'no_banner' | 'blocking' | 'non_blocking'
  days_to_block: number | null
}

export interface SubscriptionItem {
  item_price_id: string
  quantity: number
}

interface ISubscription {
  [key: string]: any
}

class Subscription extends Model<ISubscription> {
  static urlRoot() {
    return '/billing/subscriptions'
  }

  constructor(attr: ISubscription = {}) {
    super(attr)
    makeObservable(this, {
      hasScheduledUpdate: computed,
      scheduledInterval: computed,
      currentPeriodStart: computed,
      currentPeriodEnd: computed,
      inCurrentFreeTrial: computed,
      inExpiredFreeTrial: computed,
      inFreeTrial: computed,
      inPaidPlan: computed,
      isLegacy: computed,
      isPre2022Legacy: computed,
      dueBannerState: computed,
      isSelfServed: computed,
    })
  }

  get isLegacy(): boolean {
    return this.attributes.is_legacy
  }

  get hasScheduledUpdate(): boolean {
    return this.attributes.status === 'scheduled_update'
  }

  // TODO: Deprecate regex
  get scheduledInterval(): string | null | undefined {
    if (!this.hasScheduledUpdate || this.attributes.schedule.plan === undefined) return

    return this.attributes.schedule.plan.match(/-month-/) ? 'month' : 'year'
  }

  get currentPeriodStart(): moment.Moment | null {
    if (!this.attributes.schedule?.current_period_start) {
      return null
    }
    return moment(this.attributes.schedule.current_period_start)
  }

  get currentPeriodEnd(): moment.Moment | null {
    if (!this.attributes.schedule?.current_period_end) {
      return null
    }
    return moment(this.attributes.schedule.current_period_end)
  }

  cancelAndFetch(data: Record<string, any>) {
    return this.rpc('cancel_trial', data)
  }

  get inCurrentFreeTrial(): boolean {
    return this.attributes.in_current_free_trial
  }

  get inExpiredFreeTrial(): boolean {
    return this.attributes.in_expired_free_trial
  }

  get inFreeTrial(): boolean {
    return this.inCurrentFreeTrial || this.inExpiredFreeTrial
  }

  get inPaidPlan(): boolean {
    return this.attributes.in_paid_plan
  }

  get isSelfServed(): boolean {
    return !!this.attributes.self_served
  }

  get dueBannerState(): DueBannerState {
    return this.attributes.due_banner_state
  }

  get isPre2022Legacy(): boolean {
    return this.attributes.is_pre_2022_legacy
  }

  async rpcSchedulePlanChange() {
    const result = await asyncAction(() => this.rpc('schedule_plan_change'))

    if (result.success) this.set(result.value)

    return Promise.resolve(result)
  }

  async rpcChangePlanNow({ upgradePlan }: ChangePlanProps) {
    const result = await asyncAction(() =>
      this.rpc('change_plan_now', {
        new_plan_id: upgradePlan.attributes.id,
      })
    )

    if (result.success) this.set(result.value)

    return Promise.resolve(result)
  }

  get chargebeeCustomerPortal(): string {
    return this.attributes.chargebee_customer_portal
  }

  get chargebeeSubscriptionItems(): SubscriptionItem[] {
    return this.attributes.chargebee_subscription_items
  }
}

export default Subscription
